<template>
  <div class="audio-player">
    <audio v-for="(item, index) in trackItems" :class="{ active: index === currentTrackIndex }" :data-track="index" :src="item.track.url" @timeupdate="updateTime"></audio>
    <div class="audio-info">
      <div class="audio-cover">
        <img v-for="(item, index) in trackItems" :alt="item.cover.url.alt || ''" :class="{ active: currentTrackIndex === index }" :src="item.cover.url">
      </div>
      <div class="audio-compact">
        <div v-for="(item, index) in trackItems" :class="{ active: currentTrackIndex === index }" class="audio-title">
          <span class="track-title">{{ item.title }}</span>
          <span class="artist-name">{{ item.artist }}</span>
        </div>
        <div class="audio-controls mt-4 lg:mt-8">
          <div class="blank"></div>
          <div :class="{ hidden: trackItems.length === 1 }" class="button-skip-start" @click="changeTrack(-1)">
            <svg fill="currentColor" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4a.5.5 0 0 1 1 0v3.248l6.267-3.636c.54-.313 1.232.066 1.232.696v7.384c0 .63-.692 1.01-1.232.697L5 8.753V12a.5.5 0 0 1-1 0V4z"/>
            </svg>
          </div>
          <div class="button-play-pause-toggle" @click="togglePlayPause">
            <svg :class="{ hidden: this.playing }" class="play-icon" fill="currentColor" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
            </svg>
            <svg :class="{ hidden: !this.playing }" class="pause-icon" fill="currentColor" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z"/>
            </svg>
          </div>
          <div :class="{ hidden: trackItems.length === 1 }" class="button-skip-end" @click="changeTrack(1)">
            <svg fill="currentColor" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 4a.5.5 0 0 0-1 0v3.248L5.233 3.612C4.693 3.3 4 3.678 4 4.308v7.384c0 .63.692 1.01 1.233.697L11.5 8.753V12a.5.5 0 0 0 1 0V4z"/>
            </svg>
          </div>
          <div class="blank"></div>
        </div>
      </div>
    </div>
    <div class="audio-seekbar">
      <div class="timestamps">
        <span>{{ formatTime(currentTime) }}</span>
        <span>{{ formatTime(totalTime) }}</span>
      </div>
      <div class="seekbar">
        <div class="fillbar" v-bind:style="{ width: currentTime / totalTime * 100 + '%' }">
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import {uuid} from '../helpers/uuid';

export default {
  name: 'audio-player',
  props: {
    tracks: String,
  },
  data() {
    return {
      uuid: uuid(),
      currentTrackIndex: 0,
      trackItems: JSON.parse(this.tracks) ?? {},
      currentTime: 0,
      totalTime: 0,
      playing: false,
    };
  },
  methods: {
    playTrack(audioTrack) {
      if (!audioTrack.paused) return false; // The audio track is already playing

      audioTrack.play();
      this.playing = true;

      this.$eventHub.$emit('audio:started-playing', this.uuid);

      return true;
    },
    pauseTrack(audioTrack) {
      if (audioTrack.paused) return false; // The audio track is already paused

      audioTrack.pause();
      this.playing = false;

      return true;
    },
    stopTrack(audioTrack) {
      this.pauseTrack(audioTrack);
      audioTrack.currentTime = 0;
    },
    togglePlayPause() {
      let currentAudioTrack = this.getAudioTrack(this.currentTrackIndex);

      if (!this.playTrack(currentAudioTrack)) this.pauseTrack(currentAudioTrack);
    },
    changeTrack(relativePosition) {
      let newPosition = this.currentTrackIndex + relativePosition;
      let currentAudioTrack = this.getAudioTrack(this.currentTrackIndex),
          newAudioTrack = this.getAudioTrack(newPosition);

      // Throw an error when the there was no current audio track found
      if (!currentAudioTrack instanceof HTMLAudioElement)
        throw new Error('Couldn\'t change audio track, because the current audio track could not be found.');

      let wasPlaying = !currentAudioTrack.paused;

      this.stopTrack(currentAudioTrack);

      if (!newAudioTrack) {
        // The new audio track was not found, so take the one on the other site
        newPosition = relativePosition < 0 ? this.trackItems.length - 1 : 0;
        newAudioTrack = this.getAudioTrack(newPosition);
      }

      if (wasPlaying) this.playTrack(newAudioTrack);
      this.currentTrackIndex = newPosition;
    },
    getAudioTrack(position) {
      return this.$el.querySelector(`audio[data-track="${position}"]`);
    },
    updateTime(event) {
      this.currentTime = event.target.currentTime ?? 0;
      this.totalTime = event.target.duration ?? 0;
    },
    formatTime(timeInSeconds) {
      const isoDateString = new Date(timeInSeconds * 1000).toISOString(); // Format: YYYY-MM-DDTHH:MM:SS.sssZ
      const timeString = isoDateString.substr(11, 8); // -> Format: HH:MM:SS

      // Check if the hours should be shown by trying to find two zeros the first two letters
      const showHours = timeString.substr(0, 2).split('0').length - 1 !== 2;

      // Either outputs HH:MM:SS or MM:SS
      return showHours ? timeString : timeString.substr(3);
    },
  },
  mounted() {
    this.$eventHub.$on('audio:started-playing', (uuid) => {
      // Check if the audio started playing on another audio component; If so, stop this one
      if (this.uuid === uuid) return;
      this.pauseTrack(this.getAudioTrack(this.currentTrackIndex));
    });
  },
};
</script>
